import { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import { API_ENDPOINTS, apiCall, getAuthHeaders } from "../config/api";

export default function AdminProductManagement() {
  const [products, setProducts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);

  // Fetch all products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = () => {
    setLoading(true);
    apiCall(API_ENDPOINTS.PRODUCTS.ALL)
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
        Swal.fire("Error", "Failed to fetch products", "error");
      });
  };

  // Handle edit product
  const handleEdit = (product) => {
    setSelectedProduct(product);
    setEditForm({
      name: product.name,
      description: product.description,
      price: product.price,
    });
    setShowEditModal(true);
  };

  // Submit edit form
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    fetch(`http://localhost:4000/products/edit/${selectedProduct._id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(editForm),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data === true) {
          Swal.fire("Success", "Product updated successfully!", "success");
          setShowEditModal(false);
          fetchProducts(); // Refresh the list
        } else {
          Swal.fire("Error", "Failed to update product", "error");
        }
      })
      .catch((error) => {
        console.error("Error updating product:", error);
        Swal.fire("Error", "Failed to update product", "error");
      });
  };

  // Handle archive product
  const handleArchive = (productId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the product",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, archive it!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`http://localhost:4000/products/${productId}/archive`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({}),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data === true) {
              Swal.fire("Archived!", "Product has been archived.", "success");
              fetchProducts();
            } else {
              Swal.fire("Error", "Failed to archive product", "error");
            }
          })
          .catch((error) => {
            console.error("Error archiving product:", error);
            Swal.fire("Error", "Failed to archive product", "error");
          });
      }
    });
  };

  // Handle activate product
  const handleActivate = (productId) => {
    fetch(`http://localhost:4000/products/${productId}/activate`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data === true) {
          Swal.fire("Activated!", "Product has been activated.", "success");
          fetchProducts();
        } else {
          Swal.fire("Error", "Failed to activate product", "error");
        }
      })
      .catch((error) => {
        console.error("Error activating product:", error);
        Swal.fire("Error", "Failed to activate product", "error");
      });
  };

  // Handle delete product
  const handleDelete = (productId, productName) => {
    Swal.fire({
      title: "Are you sure?",
      text: `This will permanently delete "${productName}". This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete permanently!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`http://localhost:4000/products/${productId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data === true) {
              Swal.fire("Deleted!", "Product has been permanently deleted.", "success");
              fetchProducts();
            } else {
              Swal.fire("Error", "Failed to delete product", "error");
            }
          })
          .catch((error) => {
            console.error("Error deleting product:", error);
            Swal.fire("Error", "Failed to delete product", "error");
          });
      }
    });
  };

  return (
    <div className="container mt-4">
      <h1 className="mb-4">Product Management Dashboard</h1>
      
      {loading ? (
        <Alert variant="info">Loading products...</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Price</th>
              <th>Status</th>
              <th>Created On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product._id}>
                <td>{product.name}</td>
                <td>{product.description}</td>
                <td>₱{product.price}</td>
                <td>
                  <span
                    className={`badge ${
                      product.isActive ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {product.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>{new Date(product.createdOn).toLocaleDateString()}</td>
                <td>
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2 mb-1"
                    onClick={() => handleEdit(product)}
                  >
                    Edit
                  </Button>
                  {product.isActive ? (
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2 mb-1"
                      onClick={() => handleArchive(product._id)}
                    >
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      size="sm"
                      className="me-2 mb-1"
                      onClick={() => handleActivate(product._id)}
                    >
                      Activate
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    className="mb-1"
                    onClick={() => handleDelete(product._id, product.name)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Edit Product Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Product Name</Form.Label>
              <Form.Control
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={editForm.price}
                onChange={(e) =>
                  setEditForm({ ...editForm, price: e.target.value })
                }
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Product
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
